// Слэш-команды на стороне панели (README §2а) и история ввода (ui-plan п.9).

import type { CommandInfo, PastedText, PermissionMode } from '../../protocol'
import { cliApi } from '../../transport/cliApi'

/** Команды панели. Зеркало BUILTIN/LOCAL/TUI в sessions/commands.py — нужно,
 *  чтобы /usage или /mcp не ушли агенту, даже если список с сервера не доехал. */
const P = (name: string, description: string, argHint = '', source: CommandInfo['source'] = 'client'): CommandInfo => (
  { name, source, argHint, description, via: 'panel' })
const L = (name: string, description: string, argHint = '', source: CommandInfo['source'] = 'panel'): CommandInfo => (
  { name, source, argHint, description, via: 'local' })

export const PANEL_COMMANDS: CommandInfo[] = [
  P('usage', 'лимиты: окно 5 часов, неделя, расход ток/мин'),
  P('cost', 'стоимость и длительность этой сессии'),
  P('context', 'расход контекста по категориям'),
  P('model', 'показать или сменить модель', '[модель]'),
  P('permissions', 'показать или сменить режим прав', '[режим]'),
  P('status', 'модель, режим, папка, MCP, аккаунт'),
  P('compact', 'сжать разговор, освободить контекст', '[что сохранить]'),
  P('clear', 'новый разговор — процесс сессии тот же'),
  P('help', 'список команд и клавиш', '', 'panel'),
  // бывшие кнопки шапки окна (убраны 23.09.2026) — теперь командами панели
  L('restart', 'перезапустить процесс агента: разговор сохранится, MCP поднимутся заново'),
  L('save', 'сохранить беседу в заметки'),
  L('multiagent', 'включить/выключить многоагентный режим (Workflow и субагенты)'),
  L('rename', 'переименовать беседу', '<название>', 'client'),
  // интерактивные команды CLI: в headless не работают — отвечает панель
  P('mcp', 'MCP-серверы сессии: статус, переподключить, выключить, войти', '[reconnect|enable|disable|login|logout <сервер> · code <адрес>]'),
  P('agents', 'субагенты: ~/.claude/agents и проекта'),
  P('memory', 'файлы памяти: CLAUDE.md и авто-память, размеры'),
  P('resume', 'прошлые беседы — открыть'),
  P('add-dir', 'дать агенту доступ к ещё одной папке', '<путь>'),
  P('hooks', 'хуки из settings.json'),
  P('config', 'настройки Claude Code: файлы, сменить ключ', '[ключ=значение]'),
  P('release-notes', 'версия Claude Code и что нового'),
  P('login', 'вход в аккаунт Claude'),
  P('logout', 'выход из аккаунта Claude'),
  P('exit', 'выйти из Claude Code'),
  P('quit', 'выйти из Claude Code'),
  P('vim', 'режим vim в поле ввода'),
  P('terminal-setup', 'Shift+↵ для переноса строки'),
  P('theme', 'тема оформления'),
  P('color', 'цвет строки ввода', '[цвет]'),
  P('ide', 'подключение к IDE'),
  P('export', 'выгрузить беседу'),
  P('bug', 'сообщить об ошибке Claude Code'),
  P('feedback', 'отзыв о Claude Code'),
  P('upgrade', 'обновить план / Claude Code'),
  P('privacy-settings', 'настройки приватности аккаунта'),
  P('install-github-app', 'GitHub Actions для репозитория'),
  P('statusline', 'строка состояния под полем'),
  P('keybindings', 'горячие клавиши'),
  P('rewind', 'откатить разговор/код к прошлой реплике'),
  P('tasks', 'фоновые задачи и субагенты'),
  P('bashes', 'фоновые shell-команды'),
  P('todos', 'список дел агента'),
  P('plugin', 'плагины Claude Code'),
  P('heapdump', 'снимок памяти процесса CLI'),
  P('remote-control', 'управление с другого устройства'),
  P('migrate-installer', 'перенос установки CLI'),
  P('sandbox', 'песочница для bash'),
]
/** Эти выполняет сам клиент, на сервер не уходят. */
export const LOCAL_COMMANDS = new Set(PANEL_COMMANDS.filter((c) => c.via === 'local').map((c) => c.name))
export const PANEL_NAMES = new Set(PANEL_COMMANDS.filter((c) => c.via === 'panel').map((c) => c.name))
/** Встроенные команды Claude Code, которые (если процесс сессии их знает)
 *  уходят агенту как есть; если сервер не ответил списком — всё равно не
 *  «(не команда)». Если процесс их не знает, сервер отдаст via=panel. */
export const CLI_AGENT_NAMES = new Set([
  'init', 'review', 'pr-comments', 'security-review', 'doctor', 'code-review', 'simplify', 'verify',
  'debug', 'effort', 'fast', 'output-style', 'advisor', 'autocompact', 'insights', 'recap', 'goal',
  'loop', 'schedule', 'batch', 'reload-skills', 'reload-plugins', 'skill-doctor', 'list-agents',
  'ultrareview', 'usage-credits', 'import', 'team-onboarding', 'fewer-permission-prompts',
])

/** Как выполнить команду: по списку с сервера, иначе по статическим спискам. */
export function commandRoute(name: string, list: CommandInfo[]): 'local' | 'panel' | 'agent' | 'unknown' {
  if (LOCAL_COMMANDS.has(name)) return 'local'
  const hit = list.find((c) => c.name.toLowerCase() === name)
  if (hit?.via) return hit.via
  if (PANEL_NAMES.has(name)) return 'panel'
  if (hit || CLI_AGENT_NAMES.has(name)) return 'agent'
  return 'unknown'
}

/** Список с сервера + то, что знает только клиент (старый сервер без local). */
export function mergeCommands(server: CommandInfo[]): CommandInfo[] {
  const names = new Set(server.map((c) => c.name))
  const missing = PANEL_COMMANDS.filter((c) => !names.has(c.name))
  if (!missing.length) return server
  // свои команды — в начало, после команд клиента
  const head = server.filter((c) => c.via === 'panel' || c.via === 'local' || c.source === 'panel')
  const tail = server.filter((c) => !head.includes(c))
  return [...head, ...missing, ...tail]
}

export const SOURCE_LABEL: Record<CommandInfo['source'], string> = {
  client: 'клиент', panel: 'панель', skill: 'скил', command: 'команда', mcp: 'mcp',
}

/** Цикл Shift+Tab — как в Claude Code: обычный → правки → план → без ограничений. */
export const MODE_CYCLE: PermissionMode[] = ['default', 'acceptEdits', 'plan', 'bypassPermissions']

export function nextMode(mode: PermissionMode): PermissionMode {
  const i = MODE_CYCLE.indexOf(mode)
  return MODE_CYCLE[(i + 1) % MODE_CYCLE.length]
}

// ---------------- список команд с сервера (кеш на сессию) ----------------

const CACHE_MS = 60_000
const cache = new Map<string, { at: number; list: Promise<CommandInfo[]> }>()

export function loadCommands(sessionId: string, fresh = false): Promise<CommandInfo[]> {
  const hit = cache.get(sessionId)
  if (hit && !fresh && Date.now() - hit.at < CACHE_MS) return hit.list
  const list = cliApi.commands(sessionId).then(mergeCommands, () => PANEL_COMMANDS)
  cache.set(sessionId, { at: Date.now(), list })
  return list
}

/** Фильтр подсказок: сначала по началу имени, потом по вхождению, потом по описанию. */
export function filterCommands(all: CommandInfo[], query: string): CommandInfo[] {
  const q = query.toLowerCase()
  if (!q) return all
  const a: CommandInfo[] = []
  const b: CommandInfo[] = []
  const c: CommandInfo[] = []
  for (const item of all) {
    const name = item.name.toLowerCase()
    if (name.startsWith(q)) a.push(item)
    else if (name.includes(q)) b.push(item)
    else if (item.description.toLowerCase().includes(q)) c.push(item)
  }
  return [...a, ...b, ...c]
}

// ---------------- история ввода (на сессию, localStorage) ----------------

const HIST_PREFIX = 'abx-cli-hist.'
const HIST_MAX = 100

/** Запись истории: текст с токенами вставок и сами вставки (как в CLI). */
export interface HistEntry { text: string; pastes?: PastedText[] }

export function loadHistory(sessionId: string): HistEntry[] {
  try {
    const raw = localStorage.getItem(HIST_PREFIX + sessionId)
    const list = raw ? (JSON.parse(raw) as unknown) : []
    if (!Array.isArray(list)) return []
    const out: HistEntry[] = []
    for (const x of list) {
      if (typeof x === 'string') out.push({ text: x })
      else if (x && typeof x === 'object' && typeof (x as HistEntry).text === 'string') {
        const e = x as HistEntry
        out.push({ text: e.text, ...(Array.isArray(e.pastes) && e.pastes.length ? { pastes: e.pastes } : {}) })
      }
    }
    return out
  } catch {
    return []
  }
}

export function pushHistory(sessionId: string, text: string, pastes?: PastedText[]): void {
  const value = text.trim()
  if (!value) return
  const used = pastes?.filter((p) => value.includes(`[Pasted text #${p.id}`)) ?? []
  const list = loadHistory(sessionId).filter((x) => x.text !== value)
  list.push(used.length ? { text: value, pastes: used } : { text: value })
  const save = (items: HistEntry[]) => localStorage.setItem(HIST_PREFIX + sessionId, JSON.stringify(items))
  try {
    save(list.slice(-HIST_MAX))
  } catch {
    // переполнение localStorage большими вставками — храним без них
    try { save(list.slice(-HIST_MAX).map((e) => ({ text: e.text }))) } catch { /* приватный режим */ }
  }
}

// ---------------- большие вставки (как в Claude Code) ----------------

/** Порог CLI: больше 800 знаков или больше двух переводов строки. */
export const PASTE_MAX_CHARS = 800
export const PASTE_MAX_NEWLINES = 2
export const PASTE_TOKEN = /\[Pasted text #(\d+)(?: \+\d+ lines)?\]/g

export function countNewlines(text: string): number {
  return (text.match(/\r\n|\r|\n/g) ?? []).length
}

export function isBigPaste(text: string): boolean {
  return text.length > PASTE_MAX_CHARS || countNewlines(text) > PASTE_MAX_NEWLINES
}

/** Токен как в CLI: `[Pasted text #1 +41 lines]`, без переводов — `[Pasted text #1]`. */
export function pasteToken(id: number, text: string): string {
  const n = countNewlines(text)
  return n === 0 ? `[Pasted text #${id}]` : `[Pasted text #${id} +${n} lines]`
}

/** Развернуть токены (для команд панели, которые идут не через input). */
export function expandPastes(text: string, pastes: PastedText[]): string {
  if (!pastes.length) return text
  const byId = new Map(pastes.map((p) => [p.id, p.text]))
  return text.replace(PASTE_TOKEN, (m, id: string) => byId.get(Number(id)) ?? m)
}

/** Вставки, чьи токены ещё стоят в тексте. */
export function usedPastes(text: string, pastes: PastedText[]): PastedText[] {
  const ids = new Set(Array.from(text.matchAll(PASTE_TOKEN), (m) => Number(m[1])))
  return pastes.filter((p) => ids.has(p.id))
}

/** Токен, который кончается ровно в позиции pos (для Backspace) или начинается в ней (Delete). */
export function tokenAt(text: string, pos: number, dir: 'before' | 'after'): { start: number; end: number } | null {
  for (const m of text.matchAll(PASTE_TOKEN)) {
    const start = m.index ?? 0
    const end = start + m[0].length
    if (dir === 'before' && pos > start && pos <= end) return { start, end }
    if (dir === 'after' && pos >= start && pos < end) return { start, end }
  }
  return null
}

/** Вставки черновика по беседам: живут рядом с draft (в памяти вкладки). */
const draftPastes = new Map<string, PastedText[]>()

export function getDraftPastes(sessionId: string): PastedText[] {
  return draftPastes.get(sessionId) ?? []
}

export function setDraftPastes(sessionId: string, list: PastedText[]): void {
  if (list.length) draftPastes.set(sessionId, list)
  else draftPastes.delete(sessionId)
}
